import pillLogo from '@cannect/assets/img/home/pill-logo.webp'
import cannectWordmark from '@cannect/assets/svg/cannect-wordmark.svg'
import { Link, Typography } from '@cannect/new-components/atoms'
import { useRef } from 'react'
import { LuArrowLeftCircle, LuArrowRightCircle, LuCheck, LuPlusCircle } from 'react-icons/lu'
import { buildPharmacyCategoryPath, PHARMACY_BASE_PATH, PHARMACY_BENEFITS, PHARMACY_CATEGORIES } from './constants'

const CARD_CLASSES = [
  'flex h-full w-[160px] shrink-0 snap-start flex-col items-center gap-3 rounded-[16px] bg-neutral-50/40 px-3 py-10',
  'no-underline transition-colors hover:bg-neutral-50/80',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2'
].join(' ')

const ARROW_CLASSES = [
  'flex size-6 cursor-pointer appearance-none items-center justify-center border-0 bg-transparent p-0 text-neutral-800',
  'transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2'
].join(' ')

/**
 * Seção "Tudo em um só lugar: Cannect + Pill" (Figma node 4333:1160).
 *
 * Seção nova desta revisão, entre "Segurança e confiança" e o Cannect Cuida.
 *
 * O carrossel repete o mesmo conjunto de categorias que o `SubcategoryCircles` consome da API da
 * Pill — mesmos nove handles. Aqui a lista é estática porque aquele componente chega pelo PR da
 * integração Pill (#2412) e esta branch precisa compilar sozinha. Quando os dois estiverem na
 * mesma base, trocar `PHARMACY_CATEGORIES` pelo hook e apontar o `SUBCATEGORY_IMAGE_MAP` para
 * estas mesmas ilustrações, que já estão nomeadas por handle em `assets/img/subcategories`.
 *
 * O arquivo desenha as ilustrações em três caixas diferentes (largura cheia, 82x64 e 48x48), mas
 * isso vinha dos placeholders de baixa resolução: quatro categorias entraram no Figma como bitmaps
 * de 96px. Com a arte definitiva todas normalizam em 64px de altura, com a largura seguindo a
 * proporção de cada ilustração.
 *
 * O arquivo mostra sete cards porque o container recorta em 1163px; a rolagem é horizontal com
 * `scroll-snap`, e as setas empurram uma "página" de cada vez. Em telas de toque o swipe já
 * resolve, então as setas ficam escondidas abaixo de `sm`.
 *
 * O padding horizontal do card é 12px e não os 16 do arquivo: com 16, "Doenças Crônicas" fica a
 * um pixel de caber e quebra em duas linhas, o que o arquivo não faz.
 *
 * Os três benefícios são 10px SemiBold com check de 18px (node 4436:13588) — não 12px, que era o
 * palpite óbvio pelo tamanho aparente e fazia a linha não caber no container. A revisão os colocou
 * dentro de chips arredondados.
 */
export const PillPharmacySection = () => {
  const trackRef = useRef<HTMLUListElement>(null)

  const scrollBy = (direction: 1 | -1) => {
    const track = trackRef.current
    if (!track) return
    track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' })
  }

  return (
    <section aria-labelledby="pill-pharmacy-heading" className="w-full bg-marketplace-200">
      {/* 64px entre o bloco de categorias e a grade de benefícios (node 4619:487 começa em y=560,
          com o bloco de cima terminando em 496); 48px entre o cabeçalho e as categorias. */}
      <div className="mx-auto flex w-full max-w-[1230px] flex-col gap-16 px-8 py-16">
        <div className="flex flex-col gap-12">
          <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            {/* Hierarquia do arquivo (node 4333:1166): "Tudo em um só lugar:" é eyebrow de 16px e o
                H2 é a frase da farmácia, em 28px com a segunda linha em itálico. Estava invertido. */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <p className="m-0 text-base leading-[1.6] text-neutral-800">Tudo em um só lugar:</p>

                <Typography.Heading
                  id="pill-pharmacy-heading"
                  level={2}
                  weight="regular"
                  className="m-0 text-[28px] leading-[1.2] tracking-[-0.448px] text-neutral-800">
                  {/* `span`, não `em`: o itálico é assinatura de marca, não ênfase semântica. */}
                  Você não precisa mais ir até a farmácia{' '}
                  <span className="italic">porque agora a farmácia vai até você</span>
                </Typography.Heading>
              </div>

              <p className="m-0 text-lg leading-[1.4] text-neutral-800">
                Tudo o que você precisa para seu tratamento, saúde e bem-estar
                <br />
                Mais de <span className="font-semibold">17.000 itens disponíveis</span> para você
              </p>
            </div>

            {/* A revisão tirou os logos de baixo do título e levou para a direita, bem maiores,
                na mesma linha do bloco de texto — é o que encolhe a seção de 706 para 623px. */}
            <p className="m-0 flex shrink-0 items-center gap-4">
              <img src={cannectWordmark} alt="Cannect" className="h-[42px] w-[252px] shrink-0" />
              <span aria-hidden className="text-[42px] font-medium leading-[1.3] text-neutral-800">
                +
              </span>
              <img src={pillLogo} alt="Pill" className="h-[100px] w-[114px] shrink-0 object-contain" />
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <h3 className="m-0 flex-1 text-lg font-semibold leading-[1.4] text-neutral-800">Confira as categorias</h3>

              <Link
                to={PHARMACY_BASE_PATH}
                className="flex items-center gap-1 text-base font-medium leading-normal text-neutral-800 no-underline hover:underline">
                <LuPlusCircle aria-hidden className="size-5 shrink-0" />
                Ver todos
              </Link>

              <div className="hidden items-center gap-4 sm:flex">
                <button
                  type="button"
                  onClick={() => scrollBy(-1)}
                  className={ARROW_CLASSES}
                  aria-label="Categoria anterior">
                  <LuArrowLeftCircle aria-hidden className="size-6" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollBy(1)}
                  className={ARROW_CLASSES}
                  aria-label="Próxima categoria">
                  <LuArrowRightCircle aria-hidden className="size-6" />
                </button>
              </div>
            </div>

            <ul
              ref={trackRef}
              className="m-0 flex h-[171px] snap-x snap-mandatory list-none gap-[6px] overflow-x-auto p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {PHARMACY_CATEGORIES.map((category) => (
                <li key={category.handle} className="flex h-full">
                  <Link to={buildPharmacyCategoryPath(category.handle)} className={CARD_CLASSES}>
                    <img
                      src={category.image}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      decoding="async"
                      className="h-16 w-auto max-w-full shrink-0 object-contain"
                    />
                    <span className="text-center text-sm font-medium leading-[16.8px] text-neutral-800">
                      {category.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Grade de 2x2 (node 4619:487). Antes era uma fileira só de pills, com o terceiro e o
            quarto benefício fundidos num texto único; a revisão de 2026-07-31 separou os quatro em
            cards de 576x67 e a seção cresceu de 631 para 771px. */}
        <ul className="m-0 grid list-none auto-rows-fr grid-cols-1 gap-[13px] p-0 lg:grid-cols-2">
          {PHARMACY_BENEFITS.map((benefit) => (
            <li
              key={benefit}
              className="flex items-center gap-[10px] rounded-[8px] border border-solid border-marketplace-300 bg-marketplace-500/20 p-6">
              {/* Mesmo check da lista do Cannect Cuida: `marketplace-800` sobre `marketplace-300`
                  rende 4,79:1, contra os 1,41:1 da combinação do arquivo. */}
              <span
                aria-hidden
                className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-marketplace-300">
                <LuCheck className="h-[10px] w-[10px] text-marketplace-800" strokeWidth={3} />
              </span>
              <span className="text-sm font-semibold leading-[18.2px] text-neutral-800">{benefit}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
